import type { UserProfile } from "@/types/user-profile";
import {
  getUserDisplayRoleLabel,
  getUsersGroupHeader,
  type UsersTranslate,
} from "./usersLabels";

export type UsersExportRow = Record<string, string>;

export const USERS_EXPORT_FILENAME = "users-selected.csv";

const FORMULA_PREFIX = /^[=+\-@\t\r]/;

/** Neutralises spreadsheet formula injection in exported text. */
export const sanitizeCsvText = (value: string): string =>
  FORMULA_PREFIX.test(value) ? `'${value}` : value;

/** Keeps only the users whose id is selected, in table order. */
export const pickSelectedUsers = (
  users: UserProfile[],
  selectedIds: string[],
): UserProfile[] => {
  const ids = new Set(selectedIds);
  return users.filter((user) => ids.has(user.id));
};

const getGroupText = (user: UserProfile): string => {
  if (user.accessGroups && user.accessGroups.length > 0) {
    return user.accessGroups.map((group) => group.name).join(", ");
  }
  const teams = user.staff?.userTeams;
  if (teams && teams.length > 0) {
    return teams.map((ut) => ut.team.name).join(", ");
  }
  return "";
};

const getStatusText = (t: UsersTranslate, user: UserProfile): string => {
  const isStaff = user.role === "STAFF";
  const active =
    isStaff && user.staff?.isActive != null
      ? user.staff.isActive
      : user.status !== "INACTIVE";
  return active
    ? t("users.table.statusActive", "Active")
    : t("users.table.statusInactive", "Inactive");
};

/** Rows for CSV: only table columns, only selected users, table labels. */
export const buildUsersExportRows = (
  users: UserProfile[],
  selectedIds: string[],
  includeTenant: boolean,
  t: UsersTranslate,
): UsersExportRow[] =>
  pickSelectedUsers(users, selectedIds).map((user) => {
    const row: UsersExportRow = {
      [t("users.table.status", "Status")]: getStatusText(t, user),
      [t("users.table.name", "Name")]: sanitizeCsvText(
        `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
      ),
    };
    if (includeTenant) {
      row[t("users.table.tenant", "Tenant")] = sanitizeCsvText(
        user.tenant?.name ?? "",
      );
    }
    row[t("users.table.email", "Email")] = sanitizeCsvText(user.email ?? "");
    row[t("users.table.phoneNumber", "Phone number")] = sanitizeCsvText(
      user.phone ?? "",
    );
    row[t("users.table.role", "Role")] = sanitizeCsvText(
      getUserDisplayRoleLabel(t, user),
    );
    row[getUsersGroupHeader(t)] = sanitizeCsvText(getGroupText(user));
    return row;
  });
